// Per-Pal answer blocks.
//
// Every Pal page needs its own intent-matched Q&A ("direhowl palworld",
// "where to find direhowl", "how to breed direhowl") so the page can win the
// long-tail query for that Pal instead of relying on a thin stat table.
// Answers are generated from the same data the page renders, so the visible
// text and the FAQPage JSON-LD always match.

import type { FaqItem } from "@/components/PageFaq";
import { ELEMENTS, typeMultiplier, type Pal } from "@/lib/pals-data";
import { getBreedingParents, getMountInfo } from "@/lib/pals-extras";

/** Elements that hit this Pal for more than normal damage. */
function weaknesses(pal: Pal): string[] {
  return ELEMENTS.filter((e) => typeMultiplier(e, pal.element) > 1);
}

export function palFaq(pal: Pal): FaqItem[] {
  const mount = getMountInfo(pal);
  const pairs = getBreedingParents(pal, 3);
  const weak = weaknesses(pal);
  const combo = pairs[0];

  const items: FaqItem[] = [
    {
      q: `What is ${pal.name} in Palworld?`,
      a: `${pal.name} is a ${pal.rarity.toLowerCase()} ${pal.element}-type Palworld Pal (Paldeck #${pal.id})${
        pal.secondary ? ` with a secondary ${pal.secondary} typing` : ""
      }. Its partner skill is ${pal.partnerSkill}, and its work suitabilities are ${pal.work.join(", ")}.`,
    },
    {
      q: `Where can you find ${pal.name} in Palworld?`,
      a: `${pal.name} spawns around ${pal.spawn}. Use the interactive Palworld map to filter that region by element and level, then bring a Pal Sphere tier that matches ${pal.name}'s capture rate of ${pal.captureRate}/100.`,
    },
    {
      q: `How do you breed ${pal.name} in Palworld?`,
      a: combo
        ? `Put ${combo.a.name} and ${combo.b.name} of opposite gender in a Breeding Farm with Cake in the box to hatch ${pal.name}. The Palworld breeding calculator lists every other valid parent pair for ${pal.name} and the shortest route from the Pals you already own.`
        : `Use the reverse lookup in the Palworld breeding calculator: pick ${pal.name} as the target and it lists every parent pair whose combined breed power produces it.`,
    },
    {
      q: `What is ${pal.name} weak to?`,
      a: weak.length
        ? `${pal.name} is a ${pal.element} Pal, so ${weak.join(" and ")} attacks deal extra damage to it. Match those elements in your squad, and check the damage calculator for exact multipliers at your level.`
        : `${pal.name} takes normal damage from every element, so build around raw attack and the damage calculator instead of a type matchup.`,
    },
    {
      q: `Is ${pal.name} a good mount in Palworld?`,
      a: mount.canMount
        ? `Yes — ${pal.name} is a ${mount.category} mount with ${mount.flySpeed || mount.groundSpeed || mount.swimSpeed} top speed. Compare it against every other Palworld mount in the mount speed ranking before you build the saddle.`
        : `No — ${pal.name} cannot be ridden. Use it as a base worker or combat Pal and pick a dedicated mount from the Palworld mount speed ranking instead.`,
    },
  ];

  return items;
}
